// The solution's declaration in the person's words, with the platform's words
// beside them where no plain word exists (02-DESIGN/14-solutions/01-the-manifest.md
// § the manifest as the consent surface): what a declared pattern lets a
// workload do, what a capability means, a setting's scope, and the state
// words of the Solutions page (§ the console: Solutions).

import type { Capability, Manifest, Record_, Setting, SolutionRef, Workload } from "../../wire/solutions.ts";
import { PLATFORM_NAME } from "../../platform/names.ts";
import {
  Installer,
  pageOrigin,
  patternWords,
  PATTERN_WORDS,
  RecordState,
  Scope,
} from "../../wire/solutions.ts";

/** mayDo is a workload's declared verb set in words: the plain words first, the patterns they come from beside them. */
export function mayDo(w: Workload): { words: string; patterns: string[] }[] {
  const out: { words: string; patterns: string[] }[] = [];
  const add = (words: string, pattern: string) => {
    const at = out.find((o) => o.words === words);
    if (at !== undefined) {
      at.patterns.push(pattern);
    } else {
      out.push({ words, patterns: [pattern] });
    }
  };
  for (const p of w.publish ?? []) {
    add(patternWords(p) ?? `call ${p}`, p);
  }
  for (const p of w.subscribe ?? []) {
    add(patternWords(p) ?? `read ${p}`, p);
  }
  // Stopping and starting together read as one sentence.
  const stop = out.findIndex((o) => o.words === PATTERN_WORDS.suspend);
  const start = out.findIndex((o) => o.words === PATTERN_WORDS.resume);
  if (stop >= 0 && start >= 0) {
    const both = {
      words: "stop and start any workload in this workspace",
      patterns: [...(out[stop]?.patterns ?? []), ...(out[start]?.patterns ?? [])],
    };
    return [both, ...out.filter((_, i) => i !== stop && i !== start)];
  }
  return out;
}

/** capabilityWords is what sending or receiving a hierarchy capability means. */
export function capabilityWords(c: Capability, direction: "sends" | "receives"): string {
  const what: Record<Capability, string> = {
    audit: "the record of every call, never its content",
    usage: "what was used — calls and tokens, per model and app",
    control: "instructions",
  };
  return direction === "sends"
    ? `sends ${what[c]} to the workspace a grant names`
    : `receives ${what[c]} from the workspaces whose grants name this one`;
}

/** scopeWords is who sets a setting and where it lives. */
export function scopeWords(s: Setting): string {
  if (s.scope === Scope.person) {
    return "each person sets their own, under Your credentials";
  }
  return s.secret === true ? "an admin stores it as a secret" : "an admin sets it for the workspace";
}

/** settingsDefaults is the settings document an install writes: every tenant-scoped, non-secret setting's default. */
export function settingsDefaults(m: Manifest): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of m.settings ?? []) {
    if (s.scope === Scope.tenant && s.secret !== true && s.default !== undefined) {
      out[s.name] = s.default;
    }
  }
  return out;
}

/** tenantSecrets are what an admin stores under the solution's prefix: the declared secrets and the tenant-scoped secret settings. */
export function tenantSecrets(m: Manifest): { name: string; hint: string; required: boolean }[] {
  const out = (m.secrets ?? []).map((s) => ({ name: s.name, hint: s.hint, required: s.required === true }));
  for (const s of m.settings ?? []) {
    if (s.scope === Scope.tenant && s.secret === true) {
      out.push({ name: s.name, hint: s.hint ?? s.label, required: s.required === true });
    }
  }
  return out;
}

/** personSettings are the settings each person sets for themselves. */
export function personSettings(m: Manifest): Setting[] {
  return (m.settings ?? []).filter((s) => s.scope === Scope.person);
}

/** Completeness is what the console reads of an install in the workspace: what is missing, in words, empty when complete. */
export type Missing = string[];

/** stateWords is a row's state on the Solutions page. */
export function stateWords(
  r: Record_,
  missing: Missing | undefined,
  parent?: string,
): { word: string; tone: "live" | "info" | "bad" | "neutral" } {
  if (r.state === RecordState.removed) {
    return { word: "removed", tone: "neutral" };
  }
  if (r.installer === Installer.steward) {
    return { word: `required by ${parent ?? "the parent"}`, tone: "info" };
  }
  if (missing !== undefined && missing.length > 0) {
    return { word: `incomplete: ${missing[0] ?? ""}`, tone: "bad" };
  }
  if (r.latest !== undefined && r.latest > r.revision) {
    return { word: "update available", tone: "info" };
  }
  if (r.state === RecordState.installing) {
    return { word: "installing", tone: "info" };
  }
  return { word: "running", tone: "live" };
}

/** consentLine says who consented to the installed revision, and when. */
export function consentLine(r: Record_, parent?: string): string {
  if (r.installer === Installer.steward) {
    return `Required by ${parent ?? "the parent"}'s blueprint, at release ${String(r.revision)}`;
  }
  const c = [...r.consents].reverse().find((x) => x.revision === r.revision) ?? r.consents.at(-1);
  const who = c?.display ?? c?.principal ?? "an admin";
  const on = c?.at !== undefined ? c.at.slice(0, 10) : r.installed.slice(0, 10);
  return `Installed by ${who} on ${on} at release ${String(r.revision)}`;
}

/**
 * provenance is a solution workload's line on its page: *Part of
 * Observability — installed by Alice on 2026-11-02 at revision 3*, or
 * *— required by the parent's blueprint*.
 */
export function provenance(
  ref: SolutionRef,
  installed: readonly Record_[],
): { name: string; display: string; line: string } {
  const r = installed.find((x) => x.name === ref.name);
  const display = r?.manifest?.display ?? ref.name;
  const role = ` (${ref.role})`;
  if (ref.installer?.kind === Installer.steward || r?.installer === Installer.steward) {
    return {
      name: ref.name,
      display,
      line: `Part of ${display}${role} — required by the parent's blueprint, at release ${String(ref.revision)}`,
    };
  }
  const c = r?.consents.find((x) => x.revision === ref.revision) ?? r?.consents.at(0);
  const who = ref.installer?.display ?? c?.display ?? "an admin";
  const on = c?.at !== undefined ? ` on ${c.at.slice(0, 10)}` : "";
  return {
    name: ref.name,
    display,
    line: `Part of ${display}${role} — installed by ${who}${on} at release ${String(ref.revision)}`,
  };
}

/** REMOVE_CONSEQUENCE is the second confirmation of a removal. */
export const REMOVE_CONSEQUENCE =
  "Its workloads stop for good and its settings are deleted. The solution's stores and secrets are kept; delete them from Stores and Secrets if you no longer want them.";

// The declaration counted first (02-DESIGN/03-console/01-the-console.md
// § conventions: density and length; the moves, page by page: a solution's
// page): one line of who published which revision, one counted line per
// workload, and what the solution requires of the workspace as counts, each
// opening its list.

/** plural is a count with its noun, kept here so the words stay free of the components. */
function plural(n: number, one: string): string {
  return `${String(n)} ${n === 1 ? one : `${one}s`}`;
}

/**
 * publishedLine is the declaration's first line: *published by Impire · release 3 · v0.19.0* — the
 * design's *revision*, said as the solution's pages say it.
 */
export function publishedLine(publisher: string, revision: number, version?: string): string {
  const who = publisher === "platform" ? "Impire" : publisher;
  const v =
    version === undefined || version === "" ? "" : ` · ${version.startsWith("v") ? version : `v${version}`}`;
  return `published by ${who} · release ${String(revision)}${v}`;
}

/**
 * countedLine is a workload's permissions said as counts with their kinds:
 * *may do 4 things · answers as 2 · reaches 2 hosts · instructed by 1*. A
 * kind it declares none of is left out, except what it may do.
 */
export function countedLine(w: Workload): string {
  const n = mayDo(w).length;
  const parts = [n === 0 ? "may do nothing more" : `may do ${plural(n, "thing")}`];
  const names = (w.names ?? []).length;
  if (names > 0) {
    parts.push(`answers as ${String(names)}`);
  }
  const hosts = (w.egress ?? []).length;
  if (hosts > 0) {
    parts.push(`reaches ${plural(hosts, "host")}`);
  }
  const across = (w.sends ?? []).length + (w.receives ?? []).length;
  if (across > 0) {
    parts.push(`across workspaces ${String(across)}`);
  }
  if (w.controller !== undefined) {
    parts.push("instructed by 1");
  }
  const admin = (w.serves ?? []).filter((v) => v.admin === true).length;
  if (admin > 0) {
    parts.push(`${String(admin)} only an admin may`);
  }
  return parts.join(" · ");
}

/** digestOf is the digest of a reference pinned to it — `ghcr.io/x@sha256:…` — or undefined. */
export function digestOf(ref: string | undefined): string | undefined {
  const at = ref?.lastIndexOf("@") ?? -1;
  return ref !== undefined && at >= 0 ? ref.slice(at + 1) : undefined;
}

/** Requirement is one kind of thing a solution requires of the workspace, counted. */
export type RequirementKind = "settings" | "secrets" | "stores" | "fixed" | "guardrails";

/** requirements counts what the solution requires of the workspace, by kind, leaving out a kind it has none of. */
export function requirements(m: Manifest): { kind: RequirementKind; words: string; n: number }[] {
  const settings = (m.settings ?? []).filter((s) => s.secret !== true || s.scope !== Scope.tenant).length;
  const secrets = tenantSecrets(m).length;
  const stores = (m.stores ?? []).length;
  const fixed = (m.required?.documents ?? []).length;
  const rules = (m.required?.rules ?? []).length;
  const out: { kind: RequirementKind; words: string; n: number }[] = [
    { kind: "settings", words: plural(settings, "setting"), n: settings },
    { kind: "secrets", words: plural(secrets, "secret"), n: secrets },
    { kind: "stores", words: plural(stores, "store"), n: stores },
    { kind: "fixed", words: fixed === 1 ? "1 fixed setting" : `${String(fixed)} fixed settings`, n: fixed },
    { kind: "guardrails", words: plural(rules, "guardrail"), n: rules },
  ];
  return out.filter((x) => x.n > 0);
}

/**
 * PageRedirect is what an admin registers for a solution's page at its own
 * origin (02-DESIGN/09-gateway/02-the-oauth-face.md § a solution's page at its
 * own origin): the page signs a person in as a desktop AI client does, so the
 * origin its manifest declares goes on the sign-in provider's native client —
 * the one the terminal signs in with — as a redirect. The auth service stores
 * no redirect; the provider decides at its authorization endpoint, so the
 * install step lists it beside the declaration, as a person-scoped toolkit's
 * onboarding lists its redirect.
 */
export interface PageRedirect {
  origin: string;
  title: string;
  line: string;
  label: string;
}

/** pageRedirect is the page's redirect to register, or nothing for a solution without a page at an https origin. */
export function pageRedirect(m: Manifest, workspace: string): PageRedirect | undefined {
  if (m.page === undefined) {
    return undefined;
  }
  const origin = pageOrigin(m.page);
  if (origin === undefined) {
    return undefined;
  }
  const page = m.page.label !== "" ? m.page.label : m.display;
  return {
    origin,
    title: `Allow ${page}'s address at ${workspace}'s sign-in`,
    line:
      `${page} is served from its own address and signs people in the way a desktop AI client does. ` +
      `Add its address as a redirect on the sign-in provider's native client — the one the terminal and ` +
      `desktop AI clients sign in with. Where ${workspace} signs in with its own provider, add it there; ` +
      `where it signs in with ${PLATFORM_NAME}'s, ${PLATFORM_NAME}'s operator adds it.`,
    label: "Redirect, on the native client",
  };
}
